import {useQuery} from '@tanstack/react-query'
import {ArrowLeft} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {RestrictionRuleRequestCreateWizard} from '@/pages/RestrictionRuleRequestCreateWizard'
import {RestrictionRuleRequestStatus} from '@/types/restriction-rule-request'
import type {RestrictionRuleRequest} from '@/types/restriction-rule-request'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {ErrorBanner} from '@/components/ui/error-banner'
import {IconText} from '@/components/shared/IconText'
import {CONCEPT_ICON} from '@/lib/concepts'

export function RestrictionRuleRequestEdit() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.restrictionRuleRequests.editPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{id: string}>()

    const {data: request, isLoading, error} = useQuery<RestrictionRuleRequest>({
        queryKey: ['restriction-rule-request', id],
        queryFn: () => services.restrictionRuleRequests.detail(id!),
        enabled: !!id,
    })

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.restrictionRuleRequest}
            title={i18n.restrictionRuleRequests.editPageTitle}
            backTo={true}
            loading={isLoading}
            error={queryError(isLoading, request, error)}
            data-testid="restriction-rule-request-edit-page"
        >
            {request && (
                request.status !== RestrictionRuleRequestStatus.DRAFT ? (
                    <>
                        <ErrorBanner error={new Error(i18n.restrictionRuleRequests.cannotEdit({status: request.status}))}/>
                        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)} data-testid="btn-back-not-editable">
                            <IconText icon={ArrowLeft}>{i18n.common.back}</IconText>
                        </Button>
                    </>
                ) : (
                    <RestrictionRuleRequestCreateWizard
                        mode="edit"
                        requestId={id}
                        initialData={{
                            name: request.name,
                            description: request.description ?? undefined,
                            requestType: request.requestType,
                            ruleId: request.ruleId ?? undefined,
                            conditionExpr: request.conditionExpr ?? undefined,
                            deniedProfileIds: request.deniedProfileIds,
                            deniedProfileCondition: request.deniedProfileCondition ?? undefined,
                            removalGraceDays: request.removalGraceDays,
                            owners: request.owners.map(o => ({
                                ownerPartyId: o.ownerPartyId,
                                ownerPartyName: o.ownerPartyName,
                                responsibility: o.responsibility,
                            })),
                            version: request.version,
                        }}
                    />
                )
            )}
        </PageLayout>
    )
}
